/**
 * Pomocné funkce pro práci s fotkami v Supabase Storage.
 */
import { supabase } from './supabase.js';
import { compressImage } from './image-compress.js';
import { compressVideo } from './video-compress.js';

// Supabase Free tier limit: 50 MB / soubor. Pro tier: 5 GB.
const MAX_FILE_BYTES = 50 * 1024 * 1024;

function fmtMB(bytes) { return (bytes / (1024 * 1024)).toFixed(1) + ' MB'; }

function checkSize(file) {
    if (file.size > MAX_FILE_BYTES) {
        return {
            error: {
                message: `Soubor "${file.name}" má ${fmtMB(file.size)}, limit Supabase Storage je ${fmtMB(MAX_FILE_BYTES)}. ` +
                         `Použij kratší video nebo nižší kvalitu v mobilu (Nastavení → Foto/Video → 720p místo 1080p).`
            }
        };
    }
    return null;
}

let compressCache = null;
async function getCompressSettings() {
    if (compressCache) return compressCache;
    const { data } = await supabase.from('settings').select('key, value').in('key', [
        'photo_event_max_width', 'photo_event_quality',
        'photo_tech_max_width',  'photo_tech_quality'
    ]);
    const m = Object.fromEntries((data || []).map(s => [s.key, Number(s.value)]));
    compressCache = {
        eventMax:    m.photo_event_max_width || 2560,
        eventQual:  (m.photo_event_quality   || 92) / 100,
        techMax:     m.photo_tech_max_width  || 1920,
        techQual:   (m.photo_tech_quality    || 85) / 100
    };
    return compressCache;
}
export function clearCompressCache() { compressCache = null; }

export async function uploadEventPhoto(eventId, file) {
    const c = await getCompressSettings();
    const compressed = file.type.startsWith('video/')
        ? await compressVideo(file)
        : await compressImage(file, { maxWidth: c.eventMax, quality: c.eventQual });
    const sizeErr = checkSize(compressed);
    if (sizeErr) return sizeErr;

    const ext = (compressed.name.split('.').pop() || 'jpg').toLowerCase();
    const path = `${eventId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error } = await supabase.storage.from('photos-event').upload(path, compressed, {
        cacheControl: '3600', upsert: false, contentType: compressed.type
    });
    if (error) return { error };

    const { data: { user } } = await supabase.auth.getUser();
    const { error: e2 } = await supabase.from('photos').insert({
        type: 'event', event_id: eventId, storage_path: path, uploaded_by: user?.id
    });
    if (e2) return { error: e2 };
    return { path, originalSize: file.size, compressedSize: compressed.size };
}

export async function uploadTechPhoto(placeId, file) {
    const c = await getCompressSettings();
    const compressed = await compressImage(file, { maxWidth: c.techMax, quality: c.techQual });
    const sizeErr = checkSize(compressed);
    if (sizeErr) return sizeErr;

    const ext = (compressed.name.split('.').pop() || 'jpg').toLowerCase();
    const path = `${placeId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error } = await supabase.storage.from('photos-tech').upload(path, compressed, {
        cacheControl: '3600', upsert: false, contentType: compressed.type
    });
    if (error) return { error };

    const { data: { user } } = await supabase.auth.getUser();
    const { error: e2 } = await supabase.from('photos').insert({
        type: 'tech', place_id: placeId, storage_path: path, uploaded_by: user?.id
    });
    if (e2) return { error: e2 };
    return { path, originalSize: file.size, compressedSize: compressed.size };
}

export async function uploadFiles(uploadFn, id, files, onProgress, onFileStart) {
    const list = Array.from(files);
    let ok = 0, fail = 0;
    const errors = [];
    for (const file of list) {
        onFileStart?.(file);
        const r = await uploadFn(id, file);
        if (r.error) {
            fail++;
            errors.push({ name: file.name, message: r.error.message || String(r.error) });
            console.error('Upload error:', file.name, r.error);
        } else ok++;
        onProgress?.({ ok, fail, total: list.length });
    }
    return { ok, fail, errors };
}

export async function getEventPhotoCount(eventId) {
    const { count } = await supabase.from('photos').select('*', { count: 'exact', head: true })
        .eq('event_id', eventId).eq('type', 'event');
    return count || 0;
}

export async function getPlaceTechPhotoCount(placeId) {
    const { count } = await supabase.from('photos').select('*', { count: 'exact', head: true })
        .eq('place_id', placeId).eq('type', 'tech');
    return count || 0;
}

/**
 * Načte počty fotek pro seznam id najednou.
 * Vrací objekt { eventId: count } nebo { placeId: count }.
 */
export async function getPhotoCounts({ eventIds = [], placeIds = [] } = {}) {
    const result = { events: {}, places: {} };
    if (eventIds.length) {
        const { data } = await supabase.from('photos')
            .select('event_id').eq('type', 'event').in('event_id', eventIds);
        (data || []).forEach(p => {
            result.events[p.event_id] = (result.events[p.event_id] || 0) + 1;
        });
    }
    if (placeIds.length) {
        const { data } = await supabase.from('photos')
            .select('place_id').eq('type', 'tech').in('place_id', placeIds);
        (data || []).forEach(p => {
            result.places[p.place_id] = (result.places[p.place_id] || 0) + 1;
        });
    }
    return result;
}
